import { FC } from 'react';
import {
    Avatar,
    Menu,
    MenuDivider,
    MenuItem,
    MenuItemLink,
    MenuList,
    MenuPopover,
    MenuTrigger,
    Persona,
    makeStyles,
    shorthands,
    tokens,
} from '@fluentui/react-components';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { logoutAsync } from '../../../services/auth/AuthService';
import { SharedStyles, customColors } from '../../../styles';

const useClasses = makeStyles({
    avatar: {
        marginRight: tokens.spacingHorizontalXL,
        cursor: 'pointer',
        backgroundColor: customColors.protivitiDarkBlue,
        '& > img': {
            backgroundColor: 'transparent',
        },
        border: `0.01px solid ${customColors.protivitiOrange}`,
        borderRadius: '5px',
    },
    persona: {
        fontFamily: 'Lato, sans-serif !important',
        ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingVerticalMNudge),
        '& > span': {
            ...shorthands.borderRadius('0'),
            fontFamily: 'Lato, sans-serif !important',
            '& > img': {
                backgroundColor: 'transparent',
            },
        },
    },
    menu: {
        ...shorthands.borderRadius('0px'),
        top: '5px !important',
        borderRadius: '15px',
    },
    signOut: {
        ...SharedStyles.outlineButton,
    },
    personaAvatar: {
        borderRadius: '5px !important',
    },
});

export interface UserSettingsMenuProps {
    displayName: string;
    email: string;
    isAdmin: boolean;
    photoUrl?: string;
    onSigningOut?: () => void;
}

export const UserSettingsMenu: FC<UserSettingsMenuProps> = ({
    displayName,
    email,
    isAdmin,
    photoUrl,
    onSigningOut,
}) => {
    const classes = useClasses();
    const { instance } = useMsal();
    const navigate = useNavigate();

    const onLogout = async () => {
        onSigningOut?.();
        await logoutAsync({ msalInstance: instance });
    };

    return (
        <Menu>
            <MenuTrigger disableButtonEnhancement>
                <Avatar
                    className={classes.avatar}
                    key={displayName}
                    name={displayName}
                    image={{ src: photoUrl }}
                    shape="square"
                    size={32}
                />
            </MenuTrigger>

            <MenuPopover className={classes.menu}>
                <MenuList>
                    <Persona
                        className={classes.persona}
                        name={displayName}
                        secondaryText={email}
                        avatar={{
                            image: { src: photoUrl },
                            shape: 'square',
                            size: 72,
                            className: classes.personaAvatar,
                        }}
                        textAlignment="center"
                    />
                    <MenuDivider />

                    {isAdmin && <MenuItem onClick={() => navigate('/user-management')}>User Management</MenuItem>}

                    {isAdmin && <MenuItem onClick={() => navigate('/manage-agents')}>Manage Agents</MenuItem>}

                    <MenuItemLink
                        data-testid="aboutProGPTButton"
                        href="https://roberthalf.sharepoint.com/sites/iShare-GenAI/SitePages/ProGPT.aspx"
                        target="_blank"
                    >
                        About ProGPT
                    </MenuItemLink>

                    {window.top === window.self && (
                        <MenuItem data-testid="logOutMenuButton" onClick={onLogout}>
                            Sign out
                        </MenuItem>
                    )}
                </MenuList>
            </MenuPopover>
        </Menu>
    );
};

export default UserSettingsMenu;
